// ============== PER-USER LINKEDIN OAUTH ==============
// Lets ANY user connect their personal LinkedIn so scheduled posts auto-publish
// to their own feed (instead of the admin-only fallback). Separate from the
// admin "Connected platforms" card which manages the site-wide tokens.
// NOTE: LinkedIn tokens expire after ~60 days — the backend emails users a
// warning at day ~50 and this card shows an amber "Reconnect needed" state.
import {
  useState, useEffect, axios, Button, Card, CardContent, CardHeader, CardTitle,
  Badge, Loader2, API, toast,
} from "@/pages/shared";

export const LinkedInPersonalSection = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/social-connections/linkedin/status`);
      setStatus(r.data);
    } catch (e) {
      setStatus({ connected: false });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStatus();
    // If we just came back from LinkedIn OAuth, show a toast
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "linkedin") {
      toast.success("LinkedIn connected — scheduled posts will publish to your feed");
      // Clean the URL
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("linkedin_error")) {
      toast.error(`LinkedIn connection failed: ${params.get("linkedin_error")}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const r = await axios.get(`${API}/social-connections/linkedin/start`);
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error("Couldn't start LinkedIn connection");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm("Disconnect LinkedIn? Scheduled posts will fall back to reminder emails.")) return;
    setDisconnecting(true);
    try {
      await axios.post(`${API}/social-connections/linkedin/disconnect`);
      toast.success("LinkedIn disconnected");
      await fetchStatus();
    } catch (e) {
      toast.error("Couldn't disconnect");
    }
    setDisconnecting(false);
  };

  const connected = status?.connected;

  return (
    <Card className="glass" data-testid="settings-linkedin-personal-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
          Your LinkedIn account
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Connect your personal LinkedIn so scheduled posts publish directly to your feed. Without this, you&apos;ll get a reminder email instead.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : connected ? (
          <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-white/5">
            <div className="flex items-center gap-3">
              {status.picture && (
                <img src={status.picture} alt="" className="w-10 h-10 rounded-full" data-testid="linkedin-personal-avatar" />
              )}
              <div>
                <p className="font-medium" data-testid="linkedin-personal-name">{status.name || "LinkedIn user"}</p>
                <p className="text-xs text-muted-foreground" data-testid="linkedin-personal-email">{status.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-teal-500/20 text-teal-400" data-testid="linkedin-personal-badge">✓ Connected</Badge>
              <Button variant="outline" size="sm" onClick={handleDisconnect} disabled={disconnecting} data-testid="linkedin-personal-disconnect-btn">
                {disconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Disconnect"}
              </Button>
            </div>
          </div>
        ) : (status?.revoked || status?.expired) ? (
          <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <div className="flex items-center gap-3">
              {status.picture && (
                <img src={status.picture} alt="" className="w-10 h-10 rounded-full opacity-60" />
              )}
              <div>
                <p className="font-medium">{status.name || "LinkedIn user"}</p>
                <p className="text-xs text-amber-400" data-testid="linkedin-personal-expired-msg">
                  Connection {status.revoked ? "revoked" : "expired"} — scheduled posts can&apos;t publish to your feed
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-amber-500/20 text-amber-400" data-testid="linkedin-personal-expired-badge">⚠ Reconnect needed</Badge>
              <Button onClick={handleConnect} disabled={connecting} className="bg-blue-600 hover:bg-blue-700" data-testid="linkedin-personal-reconnect-btn">
                {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reconnect"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-white/5">
            <div>
              <p className="font-medium">LinkedIn not connected</p>
              <p className="text-xs text-muted-foreground">Scheduled posts will fall back to email reminders.</p>
            </div>
            <Button onClick={handleConnect} disabled={connecting} className="bg-blue-600 hover:bg-blue-700" data-testid="linkedin-personal-connect-btn">
              {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Connect LinkedIn"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
